/* eslint-disable no-param-reassign */
import { find, forEach, head, isEmpty, isNil, map, toString } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';

import L from 'leaflet';
import * as layerOps from '../../api/Layers/layer-operations';

import addLayerToLeafletMap from './addLayerToLeafletMap';
import './customLayerControl';
import 'leaflet-side-by-side';

const FULFILLED_STATUS = 'fulfilled';
let sideBySideControl;

/**
 * Adds the selected basemaps for the map.
 *
 * @async
 * @param {object} arg - The object argument.
 * @param {object} arg.layers - The layers to draw in the map.
 * @param {object} arg.baseMaps - The base maps to draw in the map.
 * @param {boolean} [arg.isUpdate=false] - Flag to determine if we are doing an update.
 * @param {boolean} [arg.layersOverlayed=true] - Flag to determine if layers are overlayed in the map.
 * @param {boolean} [arg.isSideBySide=false] - Flag to determine if the map is side by side or not.
 * @param {object} arg.layersToDisplay - The leaflet layer that contains the layers in the map.
 * @param {object} arg.leafletMap - The leaflet map object.
 * @param {object} arg.baseMapsToDisplay - The leaflet layer that contains the base maps in the map.
 * @param {Array} arg.baseMapsTransparency - An array containing the base maps that need a different transparency value.
 * @param {Array} arg.layersTransparency - An array containing the layers that need a different transparency value.
 * @param {Array} arg.baseMapsZoom - An array containing the base maps that need a different zoom value.
 * @param {Array} arg.layersZoom - An array containing the layers that need a different zoom value.
 */
const drawMapLayers = async ({
  baseMaps,
  baseMapsToDisplay,
  baseMapsTransparency,
  baseMapsZoom,
  isUpdate = false,
  layers,
  layerVisibilitySettings,
  leafletMap,
  layersToDisplay,
  layersTransparency,
  layersZoom,
  popupProp,
  layerSideBySide = {}
}) => {
  let zIndexBaseMaps = 300;
  let zIndexLayers = 500;
  const newLayersToDraw = {};
  const newBaseMapsToDraw = {};
  let MaxMaxZoom = 0;

  if (isUpdate && !isEmpty(baseMapsToDisplay))
    forEach(baseMapsToDisplay, (layer, layerName) => {
      try {
        leafletMap.removeLayer(baseMapsToDisplay[layerName]);
      } catch (error) {
        console.error(error);
      }
    });

  if (isUpdate && !isEmpty(layersToDisplay))
    forEach(layersToDisplay, (layer, layerName) => {
      try {
        leafletMap.removeLayer(layersToDisplay[layerName]);
      } catch (error) {
        console.error(error);
      }
    });

  const baseMapsPromises = map(baseMaps, (baseMap, index) => {
    zIndexBaseMaps -= 5;
    leafletMap.createPane(toString(zIndexBaseMaps));
    // We need this reassign so the zIndex of the layers is correct
    // eslint-disable-next-line no-param-reassign
    leafletMap.getPane(toString(zIndexBaseMaps)).style.zIndex = zIndexBaseMaps;

    return addLayerToLeafletMap({
      layer: baseMap,
      isAdded: index === 0,
      layersTransparency: baseMapsTransparency,
      layersZoom: baseMapsZoom,
      leafletMap,
      popupProp,
      zIndex: zIndexBaseMaps
    });
  });
  const baseMapsArray = await Promise.allSettled(baseMapsPromises);

  forEach(baseMapsArray, (baseMap, index) => {
    if (baseMap.status === FULFILLED_STATUS)
      newBaseMapsToDraw[baseMaps[index].name] = baseMap.value;
    else {
      Bert.alert(`Error loading base map: ${baseMap.reason}`, 'danger');
    }
  });

  const layersPromises = map(layers, layer => {
    zIndexLayers -= 5;
    leafletMap.createPane(toString(zIndexLayers));
    // We need this reassign so the zIndex of the layers is correct
    // eslint-disable-next-line no-param-reassign
    leafletMap.getPane(toString(zIndexLayers)).style.zIndex = zIndexLayers;
    const visibilitySetting =
      layerVisibilitySettings && !isEmpty(layerVisibilitySettings)
        ? layerVisibilitySettings.find(setting => setting.layer === layer._id)
        : { layer: layer._id, value: true };
    return addLayerToLeafletMap({
      layer,
      isAdded: visibilitySetting.value,
      layersTransparency,
      layersZoom,
      leafletMap,
      popupProp,
      zIndex: zIndexLayers
    });
  });
  const layersArray = await Promise.allSettled(layersPromises);
  forEach(layersArray, (layer, index) => {
    if (layer.status === FULFILLED_STATUS) newLayersToDraw[layers[index].name] = layer.value;
    else console.error(layer.reason);
  });

  leafletMap.zoomEndEvents = {};
  const unique = [...new Set(map(newLayersToDraw, item => item.ipsilumId))];

  forEach(newLayersToDraw, newLayer => {
    const originalLayer = find(layers, ['_id', newLayer.ipsilumId]);

    let maxZoom = layerOps.getMaxZoom(originalLayer);
    let minZoom = layerOps.getMinZoom(originalLayer);
    const layerZoom = find(layersZoom, ['id', layerOps.getId(originalLayer)]);
    if (!isNil(layerZoom)) ({ maxZoom, minZoom } = layerZoom);

    if (!layerOps.isIndoor(originalLayer)) {
      leafletMap.zoomEndEvents[layerOps.getId(originalLayer)] = event => {
        const zoom = event.target.getZoom();
        if (newLayer.options.maxZoom !== maxZoom || newLayer.options.minZoom !== minZoom) {
          newLayer.options.maxZoom = maxZoom;
          newLayer.options.minZoom = minZoom;
        }

        if (zoom > maxZoom || zoom < minZoom) leafletMap.removeLayer(newLayer);
        else if (!leafletMap.hasLayer(newLayer) && newLayer.show && unique.includes(newLayer._id)) {
          leafletMap.addLayer(newLayer);
        }
      };
      leafletMap.on('zoomend', leafletMap.zoomEndEvents[layerOps.getId(originalLayer)]);
    }
  });

  forEach(newBaseMapsToDraw, newBaseMap => {
    const originalBaseMap = find(baseMaps, ['_id', newBaseMap.ipsilumId]);

    let maxZoom = layerOps.getMaxZoom(originalBaseMap);
    let minZoom = layerOps.getMinZoom(originalBaseMap);
    const baseMapZoom = find(baseMapsZoom, ['id', newBaseMap.ipsilumId]);
    if (!isNil(baseMapZoom)) ({ maxZoom, minZoom } = baseMapZoom);
    if (newBaseMap.options.maxZoom !== maxZoom || newBaseMap.options.minZoom !== minZoom) {
      newBaseMap.options.maxZoom = maxZoom;
      newBaseMap.options.minZoom = minZoom;
    }

    if (maxZoom > MaxMaxZoom) MaxMaxZoom = maxZoom;
    if (!layerOps.isIndoor(originalBaseMap)) {
      leafletMap.zoomEndEvents[newBaseMap.ipsilumId] = event => {
        const zoom = event.target.getZoom();
        if (zoom > maxZoom || zoom < minZoom) {
          if (MaxMaxZoom !== maxZoom) {
            leafletMap.removeLayer(newBaseMap);
          }
        } else if (!leafletMap.hasLayer(newBaseMap)) leafletMap.addLayer(newBaseMap);
      };
    }
  });
  if (sideBySideControl && !isEmpty(sideBySideControl)) {
    sideBySideControl.remove();
    sideBySideControl = null;
  }
  if (layerSideBySide.state === true) {
    const layerLeftName =
      layerSideBySide.layerSide.left &&
      layers.find(layer => layer._id === layerSideBySide.layerSide.left).name;
    const leftLayer = newLayersToDraw[layerLeftName];

    const layerRightName =
      layerSideBySide.layerSide.right &&
      layers.find(layer => layer._id === layerSideBySide.layerSide.right).name;
    const rightLayer = newLayersToDraw[layerRightName];

    if (leftLayer && rightLayer) {
      sideBySideControl = L.control.sideBySide(leftLayer, rightLayer).addTo(leafletMap);
    } else {
      Bert.alert('No hay capas suficientes para activar este control.', 'warning');
    }
  }
  const activeBaseMap = head(baseMaps);
  if (!isNil(activeBaseMap)) {
    leafletMap.activeBaseMapZoomEndEvent = leafletMap.zoomEndEvents[layerOps.getId(activeBaseMap)];
    leafletMap.on('zoomend', leafletMap.activeBaseMapZoomEndEvent);
  }

  return { newLayersToDraw, newBaseMapsToDraw };
};

export default drawMapLayers;
